"use client";

import { activeBadgeIsGapForecast, verdictsWithGapForecast } from "./gap-forecast-verdict";
import type { GapForecastInfo } from "@/lib/conditions/gap-forecast-display";
import { Fragment, useState, useMemo } from "react";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import dynamic from "next/dynamic";
import type { SkiDetail, SportDetail } from "@/lib/conditions/detail";
import type { ConditionNote } from "@/lib/conditions/condition-notes";
import type { NormalisedHour } from "@/lib/conditions/daily-obs-types";
import type { HeatmapDataset } from "@/app/_components/conditions/SeasonHeatmap";
import type { TouringDailyResult } from "@/lib/conditions/ski-touring-scoring-v4";
import type { SkiElevationData } from "@/lib/conditions/ski-elevation";
import type { AirQualityData } from "@/lib/conditions/air-quality";
import type { SkiScoredDay } from "@/lib/conditions/detail";
import type { LuckFactor } from "@/lib/conditions/luck";
import { MountainSportFocus } from "@/app/_components/conditions/SportFocus";
import { ForecastStatus } from "@/app/_components/conditions/ForecastStatus";
import { SessionWindowSummary } from "@/app/_components/conditions/SessionWindowSummary";
import { evidenceVerdict, forecastIsStale, forecastSafety, upcomingForecast } from "@/lib/conditions/forecast-presentation";
import { skiScoreConfidenceIsPublishable } from "@/lib/conditions/ski-score-confidence";
import { SeasonOverview } from "@/app/_components/conditions/SeasonOverview";
import { SpotOverview } from "@/app/_components/conditions/SpotOverview";
import { SeasonHeatmap } from "@/app/_components/conditions/SeasonHeatmap";
import { URL_TO_INTERNAL_SPORT } from "@/lib/conditions/unified-types";
import type { UniversalEnhancements, OffPisteEnhancements, TouringEnhancements, MtbEnhancements, PerSportSeasonStatus, SportBadge, SportEligibilityNotices } from "@/lib/conditions/unified-types";
import { buildSkiMetrics, buildMtbMetrics } from "@/lib/conditions/metric-strip";
import { buildVisHourSeries } from "@/lib/conditions/visibility-strip";
import type { SafetyHeadsUpInput, SafetySeverity } from "@/lib/conditions/safety-headsup";
import { orderLocationHouseSlots, type HouseId } from "@/lib/conditions/location-houses";

import { SportSelector } from "@/app/_components/conditions/SportSelector";
import { VerdictBlock, type VerdictSportData } from "@/app/_components/conditions/VerdictBlock";
import { SafetyHeadsUp } from "@/app/_components/conditions/SafetyHeadsUp";
import { HouseTabs } from "@/app/_components/conditions/HouseTabs";
import { MountainMapInvite } from "@/app/_components/planner/MountainMapEntry";
import { TrendBadge } from "@/app/_components/conditions/TrendBadge";
import { RarityBadge } from "@/app/_components/conditions/RarityBadge";
import { SkiForecastStrip } from "@/app/_components/conditions/ForecastStrip";
import { SkiElevationPanel } from "@/app/_components/conditions/SkiElevationPanel";
import { AirQualityPanel } from "@/app/_components/conditions/AirQualityPanel";
import { shouldShowAirQuality } from "@/lib/conditions/air-quality-visibility";
import { generatePackingNotes } from "@/lib/conditions/scoring";
import type { PackingSport } from "@/lib/conditions/scoring";
import { buildMountainForecastDays } from "./forecast-surface-transport";
import { ExpandableMountainScene } from "@/app/_components/weather-scene/ExpandableMountainScene";
import { parseMountainSummary, type MountainVisualiserForecast } from "@/lib/conditions/visualiser/mountain-forecast";
import { offsetIsoDate } from "@/lib/conditions/spot-locality";

const PisteInsights = dynamic(() => import("@/app/_components/conditions/insights/PisteInsights").then(m => m.PisteInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const OffPisteInsights = dynamic(() => import("@/app/_components/conditions/insights/OffPisteInsights").then(m => m.OffPisteInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const TouringInsights = dynamic(() => import("@/app/_components/conditions/insights/TouringInsights").then(m => m.TouringInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
const MtbInsights = dynamic(() => import("@/app/_components/conditions/insights/MtbInsights").then(m => m.MtbInsights), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-border/20 rounded-[14px] h-[300px]" />,
});
import { SportHourlyTimeline } from "@/app/_components/conditions/SportHourlyTimeline";
import type { HourlyScore } from "@/lib/conditions/session-window-universal";
import { scoreExplanationForDay, type SportScoreExplanation } from "@/lib/conditions/factor-explanations";
import { SeasonBanner } from "@/app/_components/conditions/SeasonBanner";
import TrailForecastRow from "@/app/_components/conditions/TrailForecastRow";
import { DisclosurePanel } from "@/app/_components/conditions/DisclosurePanel";
import { SafetyDisclaimer } from "@/app/_components/SafetyDisclaimer";
import type { ReactNode } from "react";
import { elevationBandColumn } from "@/lib/conditions/elevation-band-column";
import { mtbSoilMoistureLabel } from "@/lib/conditions/mtb-soil-moisture";
import { FactorHeadlineSummary } from "@/app/_components/conditions/insights/FactorFirst";
import { MtbGroundTruthLocationProvider } from "@/app/_components/conditions/MtbGroundTruthPanel";
import { ForecastAccuracy } from "@/app/_components/conditions/ForecastAccuracy";
import { scoreOutOf100, scoreOutOf100Label } from "@/lib/conditions/score-display";
import type { SurfRecentForm } from "@/lib/conditions/detail";
import type { SpotAccuracy } from "@/lib/conditions/forecast-accuracy";
import type { SkiSport } from "@/lib/conditions/ski/types";
import { resolveSlotSport } from "@/lib/conditions/sport-aware-slot";
import { SkiStatusHeadline } from "@/app/_components/conditions/ski/SkiStatusHeadline";
import { SkiHeadlineDetails } from "@/app/_components/conditions/ski/SkiHeadlineDetails";
import { SkiBandsPanel } from "@/app/_components/conditions/ski/SkiBandsPanel";
import {
  SKI_SPORTS,
  headlineCover,
  isSkiSportKey,
  skiExtentSummary,
  skiV3View,
  skiV3WindowHours,
  todayScoreProvenance,
  withWithheldHoursNulled,
  type SkiV3View,
} from "@/app/_components/conditions/ski/ski-v3-view";
import { intlTimeZoneAt } from "@/lib/time-zone-rules";

/* ─── Helpers ─── */

const SEVERITY: Record<"green" | "amber" | "red", SafetySeverity> = {
  green: "none",
  amber: "caution",
  red: "danger",
};

type UnifiedMountainProps = {
  locationSlug: string;
  skiDetail: SkiDetail;
  mtbDetail: SportDetail | null;
  touringResult: TouringDailyResult | null;
  touringSunrise: number | null;
  touringSunset: number | null;
  offPisteFactors: Record<string, number> | null;
  offPisteElevationBands: import("@/lib/conditions/offpiste-scoring-v5").OffPisteElevationBand[] | null;
  touringElevationBands: import("@/lib/conditions/ski-touring-scoring-v4").TouringElevationBand[] | null;
  conditionNotes: Record<string, ConditionNote>;
  heatmapDatasets: HeatmapDataset[];
  hourlyJsonb: NormalisedHour[] | null;
  yesterdayJsonb: NormalisedHour[] | null;
  tomorrowJsonb: NormalisedHour[] | null;
  spotName: string;
  country: string;
  longitude: number;
  timezone?: string | null;
  todayISO: string;
  badges: SportBadge[];
  /** The nightly gap: the badges carry the previous run's forecast for today (lib/conditions/gap-forecast.ts). */
  gapForecast?: GapForecastInfo | null;
  defaultSport: string;
  skiForecast: SkiScoredDay[];
  bestForecastDate: string | null;
  busyPeriodLabel: string | null;
  elevationData: SkiElevationData | null;
  airQuality: AirQualityData | null;
  weatherLabel: string;
  description: string | null;
  sportHourlyScores: Record<string, HourlyScore[]>;
  sportScoreExplanations: Record<string, SportScoreExplanation>;
  yesterdaySportScores: Record<string, HourlyScore[]>;
  tomorrowSportScores: Record<string, HourlyScore[]>;
  universalEnhancements: Record<string, UniversalEnhancements>;
  offPisteEnhancements: OffPisteEnhancements | null;
  touringEnhancements: TouringEnhancements | null;
  mtbEnhancements: MtbEnhancements | null;
  seasonWindow?: { start: string; end: string } | null;
  perSportSeasonStatus: PerSportSeasonStatus;
  forecastAccuracy: SpotAccuracy | null;
  /** Server-rendered (non sport-aware) sections, placed into the house panels. */
  forecastPanelSlot?: ReactNode;
  mapPanelSlot?: ReactNode;
  mtbGroundTruthForecastSlot?: ReactNode;
  mtbGroundTruthCommunitySlot?: ReactNode;
  spotLocalSlot?: ReactNode;
  /** The area block ("Elsewhere in {Area} today" and the verdict line), directly under the verdict. */
  areaSlot?: ReactNode;
  spotCommunitySlot?: ReactNode;
  seasonPanelSlot?: ReactNode;
  /** Why MTB is not ranked here (curated venue eligibility); absent unless the flag lists mtb. */
  sportEligibilityNotices?: SportEligibilityNotices;
  /** Server-rendered evidence line under MTB's score (its confidence badge); absent unless it has one. */
  sportEvidenceSlots?: Partial<Record<string, ReactNode>>;
};

export function UnifiedMountain({
  locationSlug,
  skiDetail,
  mtbDetail,
  touringResult,
  touringSunrise,
  touringSunset,
  offPisteFactors,
  offPisteElevationBands,
  touringElevationBands,
  heatmapDatasets,
  hourlyJsonb,
  yesterdayJsonb,
  tomorrowJsonb,
  spotName,
  country,
  longitude,
  timezone,
  todayISO,
  badges,
  gapForecast = null,
  defaultSport,
  skiForecast,
  bestForecastDate: _bestForecastDate,
  busyPeriodLabel,
  elevationData,
  airQuality,
  weatherLabel: _weatherLabel,
  description,
  sportHourlyScores,
  sportScoreExplanations,
  yesterdaySportScores,
  tomorrowSportScores,
  universalEnhancements,
  offPisteEnhancements,
  touringEnhancements,
  mtbEnhancements,
  seasonWindow,
  perSportSeasonStatus,
  forecastAccuracy,
  forecastPanelSlot,
  mapPanelSlot,
  mtbGroundTruthForecastSlot,
  mtbGroundTruthCommunitySlot,
  spotLocalSlot,
  areaSlot,
  spotCommunitySlot,
  seasonPanelSlot,
  sportEligibilityNotices,
  sportEvidenceSlots,
}: UnifiedMountainProps) {
  // Post-hydration param read keeps this statically rendered page's full
  // HTML in the initial response (useSearchParams would swap it for the
  // Suspense fallback)
  const sportParam = useShallowSearchParam("sport");
  const urlSport = sportParam ?? defaultSport;
  const activeSport = URL_TO_INTERNAL_SPORT[urlSport] ?? urlSport;
  const isMtb = activeSport === "mtb";
  const activeEnhancements = universalEnhancements[activeSport] ?? null;
  const elevationColumn = elevationData
    ? elevationBandColumn(activeSport, offPisteElevationBands, touringElevationBands, elevationData.tiers)
    : { tierScores: null, sportLabel: null };

  // Ski v3 fields per sport (empty on revision 2 rows, so every v3 element below stays hidden).
  const skiV3BySport = skiDetail.skiV3BySport ?? {};
  const skiProvenance = todayScoreProvenance(skiDetail);
  const skiViews: Partial<Record<SkiSport, SkiV3View>> = {};
  for (const sport of SKI_SPORTS) {
    const view = skiV3View(sport, skiV3BySport[sport], skiProvenance);
    if (view) skiViews[sport] = view;
  }
  const activeSkiView = isSkiSportKey(activeSport) ? skiViews[activeSport] ?? null : null;
  const hasSkiBands = SKI_SPORTS.some((sport) => (skiViews[sport]?.fields.bands?.length ?? 0) > 0);

  type DayKey = "yesterday" | "today" | "tomorrow";
  const [activeDay, setActiveDay] = useState<DayKey>("today");
  const dayHourlyMap: Record<DayKey, NormalisedHour[] | null> = {
    yesterday: yesterdayJsonb,
    today: hourlyJsonb,
    tomorrow: tomorrowJsonb,
  };
  const dayScoresMap: Record<DayKey, Record<string, HourlyScore[]>> = {
    yesterday: yesterdaySportScores,
    today: sportHourlyScores,
    tomorrow: tomorrowSportScores,
  };
  const activeHourly = dayHourlyMap[activeDay];
  const insightHours = activeSport === "mtb" ? activeHourly : hourlyJsonb;
  const activeScores = dayScoresMap[activeDay];
  // Stored v3 lean points mark withheld hours with `st` (the timeline shows "Closed") and may
  // carry lo/hi; a withheld hour reads as no score, never 0, in the window summary too.
  const rawActivePoints = activeScores[activeSport];
  const activePoints = rawActivePoints ? withWithheldHoursNulled(rawActivePoints) : undefined;
  const hasDayToggle = !!yesterdayJsonb || !!tomorrowJsonb;

  // Hours in the stored data are already in the location's local time. The zone comes from
  // intlTimeZoneAt so a browser whose data predates tzdata 2026e still shows today's wall clock.
  const nowForLocal = new Date();
  const localParts = timezone ? new Intl.DateTimeFormat("en-GB", {
    timeZone: intlTimeZoneAt(timezone, nowForLocal.getTime()), hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(nowForLocal) : null;
  const utcOffsetHours = Math.round(longitude / 15);
  const localHour = localParts
    ? Number(localParts.find((part) => part.type === "hour")?.value ?? 0) % 24
    : (new Date().getUTCHours() + utcOffsetHours + 24) % 24;
  const localMinute = localParts
    ? Number(localParts.find((part) => part.type === "minute")?.value ?? 0)
    : new Date().getUTCMinutes();
  const localTimeLabel = `${localHour % 12 === 0 ? 12 : localHour % 12}:${localMinute.toString().padStart(2, "0")}${localHour < 12 ? "am" : "pm"}`;

  const activeHeatmapDatasets = heatmapDatasets.filter(ds => ds.key === activeSport);

  // Per-sport luck map passed to VerdictBlock for the inline chip.
  // Mirrors the activeLuck switch above exactly.
  const perSportLuck: Record<string, LuckFactor | null> = {
    piste: skiDetail.luck,
    "off-piste": skiDetail.luck,
    touring: skiDetail.touringLuck,
    mtb: skiDetail.mtbLuck,
  };

  const activePackingNotes = useMemo(() => {
    if (activeSport === "mtb" && mtbDetail) return mtbDetail.packingNotes;
    if (activeSport === "piste") return skiDetail.packingNotes;
    return generatePackingNotes(
      skiDetail.forecast.filter(d => Number.isFinite(d.tempMinC) && Number.isFinite(d.tempMaxC)).map((d) => ({
        tempMinC: d.tempMinC,
        tempMaxC: d.tempMaxC,
        snowfallCm: d.snowfallCm,
        windSpeedKmh: d.windSpeedKmh,
        weatherCode: d.weatherCode,
      })),
      activeSport as PackingSport
    );
  }, [activeSport, skiDetail, mtbDetail]);
  const activeUpdated = isMtb ? mtbDetail?.lastUpdated : skiDetail.lastUpdated;
  const activeStale = forecastIsStale(activeUpdated, isMtb ? mtbDetail?.isStale : skiDetail.isStale);
  const upcomingDays = upcomingForecast(skiForecast, todayISO);
  const currentExplanation = sportScoreExplanations[activeSport] ?? null;

  /* ─── Per-sport verdict + safety maps (consumed by the shell) ─── */

  const r = skiDetail.raw;

  const visHours = hourlyJsonb ? buildVisHourSeries(hourlyJsonb) : [];
  const visNow = visHours.find((h) => h.hour === localHour) ?? visHours.find((h) => h.isDay) ?? null;

  function skiMetrics(sport: SkiSport): VerdictSportData["metrics"] {
    // v3: the scored cover word replaces the fetch-point model depth (consumer inventory 8.7).
    const cover = headlineCover(skiViews[sport]?.fields.bands);
    const cells = buildSkiMetrics({
      snowfallCm48h: r.snowfallCm48h ?? null,
      snowDepthCm: cover ? null : r.snowDepthCm ?? null,
      tempMinC: r.tempMinC ?? null,
      tempMaxC: r.tempMaxC ?? null,
      windSpeedKmh: r.windSpeedKmh ?? null,
      freezingLevelM: r.freezingLevelM ?? null,
      visibilityLabel: visNow?.label ?? null,
      visibilityHours: visHours,
    });
    if (cover) cells.splice(Math.min(3, cells.length), 0, { label: "Cover", value: cover });
    return cells;
  }

  function mtbMetrics(): VerdictSportData["metrics"] {
    if (!mtbDetail) return [];
    return buildMtbMetrics({
      apparentTempC: mtbDetail.apparentTempC ?? null,
      soilMoistureLabel: mtbSoilMoistureLabel(mtbDetail.soilMoisture),
      windSpeedKmh: mtbDetail.windSpeedKmh ?? 0,
      precipMm24h: mtbDetail.precipMm24h ?? null,
    });
  }

  // Piste "Ski of the day" recommendation, promoted into the verdict's
  // tactical line. Split days surface the morning pick (the actionable one
  // first thing); single recommendations surface the headline.
  const pisteTacticalLine = (() => {
    const rec = skiDetail.luck?.recommendation;
    if (!rec) return null;
    if (!rec.body) return null;
    if (rec.splitDay) {
      return `Ski of the day (morning): ${rec.splitDay.morning.title}. ${rec.splitDay.morning.body}`;
    }
    return `Ski of the day: ${rec.title}. ${rec.body}`;
  })();

  const perSportVerdict: Record<string, VerdictSportData> = {};
  const perSportSafety: Record<string, SafetyHeadsUpInput> = {};

  // Avalanche + storm hazards shared across the snow disciplines.
  const avalancheHazard: { severity: SafetySeverity; label: string } | null =
    skiDetail.avalancheSafety && skiDetail.avalancheSafety.level !== "green"
      ? { severity: SEVERITY[skiDetail.avalancheSafety.level], label: skiDetail.avalancheSafety.label }
      : null;
  const stormHazard: { severity: SafetySeverity; label: string } | null =
    skiDetail.stormHazard
      ? { severity: SEVERITY[skiDetail.stormHazard.level], label: skiDetail.stormHazard.label }
      : null;

  // Piste
  if (skiDetail.pisteScore != null) {
    perSportVerdict.piste = {
      label: skiDetail.pisteLabel ?? skiDetail.conditionsLabel,
      score: skiDetail.pisteScore,
      note: evidenceVerdict(sportScoreExplanations.piste),
      metrics: skiMetrics("piste"),
      tacticalLine: pisteTacticalLine,
    };
    // Piste safety stays beside the score. Avalanche remains an
    // off-piste/backcountry concern, but cold, wind and lightning matter on lifts.
    const pisteHazards = (skiDetail.pisteSafety ?? [])
      .filter((s) => s.severity !== "green")
      .map((s) => ({ severity: SEVERITY[s.severity], label: s.label }));
    perSportSafety.piste = {
      hazards: [...pisteHazards, ...(stormHazard ? [stormHazard] : [])],
    };
  }

  // Off-piste
  if (skiDetail.offpisteScore != null) {
    perSportVerdict["off-piste"] = {
      label: skiDetail.offpisteLabel ?? skiDetail.conditionsLabel,
      score: skiDetail.offpisteScore,
      note: evidenceVerdict(sportScoreExplanations["off-piste"]),
      metrics: skiMetrics("off-piste"),
      tacticalLine: null,
    };
    perSportSafety["off-piste"] = {
      hazards: [avalancheHazard, stormHazard].filter(
        (h): h is { severity: SafetySeverity; label: string } => h != null
      ),
    };
  }

  // Touring
  if (skiDetail.touringScore != null) {
    perSportVerdict.touring = {
      label: skiDetail.touringLabel ?? skiDetail.conditionsLabel,
      score: skiDetail.touringScore,
      note: evidenceVerdict(sportScoreExplanations.touring),
      metrics: skiMetrics("touring"),
      tacticalLine: null,
    };
    // Touring's own safety layer (ridge wind, extreme cold, …) plus the shared
    // avalanche read.
    const touringHazards = (touringResult?.safety ?? [])
      .filter((s) => s.severity !== "green")
      .map((s) => ({ severity: SEVERITY[s.severity], label: s.label }));
    perSportSafety.touring = {
      hazards: [...(avalancheHazard ? [avalancheHazard] : []), ...touringHazards],
    };
  }

  // MTB
  if (mtbDetail) {
    perSportVerdict.mtb = {
      label: mtbDetail.label,
      score: mtbDetail.score,
      note: mtbSoilMoistureLabel(mtbDetail.soilMoisture) ? `Modelled trail surface: ${mtbSoilMoistureLabel(mtbDetail.soilMoisture)?.toLowerCase()}. Check operator and rider reports below for local conditions.` : "A local trail-surface assessment is unavailable.",
      metrics: mtbMetrics(),
      tacticalLine: null,
      ...(sportEligibilityNotices?.mtb ? { eligibilityNotice: sportEligibilityNotices.mtb } : {}),
      ...(sportEvidenceSlots?.mtb ? { evidenceSlot: sportEvidenceSlots.mtb } : {}),
    };
    perSportSafety.mtb = {
      hazards: (mtbDetail.mtbSafety ?? [])
        .filter((s) => s.severity !== "green")
        .map((s) => ({ severity: SEVERITY[s.severity], label: s.label })),
    };
  }

  // v3 Closed or no lift access: the headline reads the status word and never a number, and
  // the luck chip is dropped (owner decision). Revision 2 rows have no status.
  for (const sport of SKI_SPORTS) {
    const status = skiViews[sport]?.status;
    if (!status) continue;
    perSportVerdict[sport] = {
      label: status.label,
      score: null,
      note: null,
      metrics: skiMetrics(sport),
      tacticalLine: null,
      unavailableReason: `${status.label}. ${status.reason}`,
    };
    perSportLuck[sport] = null;
  }

  for (const sport of ["piste", "off-piste", "touring", "mtb"] as const) {
    const stale = forecastIsStale(sport === "mtb" ? mtbDetail?.lastUpdated : skiDetail.lastUpdated, sport === "mtb" ? mtbDetail?.isStale : skiDetail.isStale);
    const evidence = sport === "mtb" ? mtbDetail?.scoreEvidence : skiDetail.scoreEvidenceBySport?.[sport] ?? skiDetail.scoreEvidence;
    perSportSafety[sport] = forecastSafety(perSportSafety[sport], sportScoreExplanations[sport], evidence, stale);
    const confidence = sport === "mtb" ? null : skiDetail.snowDepthConfidenceBySport?.[sport];
    const unsupported = sport !== "mtb" && !skiScoreConfidenceIsPublishable(confidence?.confidence);
    if (perSportVerdict[sport] && (stale || unsupported)) {
      perSportVerdict[sport].score = null;
      perSportVerdict[sport].unavailableReason = stale ? "Historical readings, awaiting a fresh forecast." : "A current score needs reliable snow-depth evidence. Weather readings are shown for context.";
      perSportVerdict[sport].note = null;
      perSportVerdict[sport].tacticalLine = null;
      perSportLuck[sport] = null;
    }
  }

  // The sport the verdict shows (VerdictBlock resolves it the same way). buildSkiBadges keeps a
  // withheld off-piste or touring badge (null score with its Closed or No lift access label),
  // so ?sport=off-piste resolves in every badge-resolved slot. The active ski sport with a v3
  // status is still preferred here, so the verdict keeps that sport's own Closed or No lift
  // access headline, matching the insights below, whatever the badge list resolves to.
  const resolvedVerdictSport = resolveSlotSport(sportParam ?? defaultSport, defaultSport, badges.map(({ sport }) => sport));
  const verdictSport = isSkiSportKey(activeSport) && skiViews[activeSport]?.status ? activeSport : resolvedVerdictSport;
  const verdictSkiView = isSkiSportKey(verdictSport) ? skiViews[verdictSport] ?? null : null;
  const verdictClosed = !activeStale && verdictSkiView?.status ? verdictSkiView.status : null;
  const pisteExtent = skiExtentSummary(
    skiViews.piste?.fields.extent,
    skiViews.piste?.quality ?? null,
    skiDetail.pisteScore == null ? null : scoreOutOf100(skiDetail.pisteScore),
  );
  const officialLink = skiDetail.officialStatusLink ?? null;

  /* ─── Forecast house: sport-aware client sections ─── */

  const mountainForecast: MountainVisualiserForecast = {
    location: { slug: locationSlug, name: spotName, country, region: skiDetail.region,
      latitude: skiDetail.latitude, longitude, timezone: timezone ?? null, elevationM: skiDetail.elevationM ?? null },
    days: Array.from({ length: 7 }, (_, index) => {
      const date = offsetIsoDate(todayISO, index);
      const hours = index === 0 ? hourlyJsonb ?? [] : index === 1 ? tomorrowJsonb ?? [] : [];
      return { date, hours,
        // Hourly issue times and stored-curve provenance are absent from the
        // existing page props. Do not borrow the daily carrier's freshness or
        // publish a potentially recomputed curve as a trusted stored rating.
        updatedAt: hours.length ? null : skiDetail.lastUpdated ?? null,
        hourlyScores: {},
        summary: parseMountainSummary(skiForecast.find((item) => item.date === date)),
      };
    }),
  };

  const forecastPanel = (
    <div className="py-8 md:py-10">
      {orderLocationHouseSlots<"forecast", ReactNode>("forecast", {
      "today-hourly": (
      <>
      <div id="section-hourly" data-house-section="today-hourly" className="scroll-mt-28">
        {!activeStale && activeScores[activeSport] && activeHourly && activeHourly.length > 0 && (
          <div className="bg-white rounded-[18px] border border-border-light p-5 md:p-7">
            <div className="flex items-baseline justify-between mb-1">
              <h2 className="font-serif text-[20px] md:text-[24px] font-bold text-navy">
                {activeDay === "today" ? "Today's forecast" : activeDay === "yesterday" ? "Yesterday" : "Tomorrow"}
              </h2>
              <span className="text-[12px] text-text-muted">Local time {localTimeLabel}</span>
            </div>
            <SessionWindowSummary
              scores={activePoints ?? []}
              duration={activeSkiView?.cls && isSkiSportKey(activeSport) ? skiV3WindowHours(activeSport) : activeSport === "touring" ? 5 : 2}
              localHour={activeDay === "today" ? localHour + localMinute / 60 : undefined}
              blocked={activeStale || perSportVerdict[activeSport]?.score == null || perSportSafety[activeSport]?.dataState !== "present" || perSportSafety[activeSport]?.checksIncomplete || perSportSafety[activeSport]?.hazards.some(({ severity }) => severity === "danger")}
            />
            {hasDayToggle && (
              <div className="flex gap-1.5 mb-4" role="group" aria-label="Select day">
                {([["yesterday", "Yesterday"], ["today", "Today"], ["tomorrow", "Tomorrow"]] as const).map(([key, label]) => {
                  const hasData = !!dayHourlyMap[key]?.length;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => hasData && setActiveDay(key)}
                      disabled={!hasData}
                      aria-pressed={activeDay === key}
                      className={`min-h-11 rounded-full px-3.5 py-1.5 text-[12px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 ${
                        activeDay === key
                          ? "bg-navy text-white"
                          : hasData
                            ? "bg-warm-bg text-text-muted hover:bg-stone-200"
                            : "bg-warm-bg text-text-muted/40 cursor-not-allowed"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            <SportHourlyTimeline
              scores={perSportVerdict[activeSport]?.score == null ? activeScores[activeSport].map((point) => ({ ...point, score: null })) : activePoints ?? []}
              hours={activeHourly}
              sport={activeSport}
              localHour={activeDay === "today" ? localHour : undefined}
            />
          </div>
        )}
      </div>
      </>
      ),

      "factor-explanation": !activeStale && insightHours && insightHours.length > 0 ? (
        <div data-house-section="factor-explanation">
          <DisclosurePanel title="Why this score" id="section-insights">
          {activeSport === "piste" && <PisteInsights hourlyData={insightHours} skiDetail={skiDetail} snowDepthCm={skiDetail.raw.snowDepthCm ?? undefined} adequateBaseCm={skiDetail.adequateBaseCm} snowHistory={skiDetail.snowHistory} />}
          {activeSport === "off-piste" && <OffPisteInsights hourlyData={insightHours} skiDetail={skiDetail} crustRisk={offPisteEnhancements?.crustRisk} enhancements={offPisteEnhancements} snowDepthCm={skiDetail.raw.snowDepthCm ?? undefined} adequateBaseCm={skiDetail.adequateBaseCm} snowHistory={skiDetail.snowHistory} avalancheSafety={skiDetail.avalancheSafety} stormHazard={skiDetail.stormHazard} country={country} factors={offPisteFactors} />}
          {activeSport === "touring" && (touringResult || skiViews.touring) && (
            <TouringInsights hourlyData={insightHours} touringResult={touringResult} skiDetail={skiDetail} enhancements={touringEnhancements} snowDepthCm={skiDetail.raw.snowDepthCm ?? undefined} adequateBaseCm={skiDetail.adequateBaseCm} snowHistory={skiDetail.snowHistory} country={country} sunriseHour={touringSunrise} sunsetHour={touringSunset} avalancheSafety={skiDetail.avalancheSafety} />
          )}
          {activeSport === "mtb" && mtbDetail && (
            <MtbInsights
              hourlyData={insightHours}
              mtbDetail={mtbDetail}
              enhancements={mtbEnhancements}
              explanation={scoreExplanationForDay(activeDay, sportScoreExplanations, "mtb")}
              isCurrentDay={activeDay === "today"}
            />
          )}
          </DisclosurePanel>
        </div>
      ) : null,

      tactical: !activeStale && ((activeEnhancements && (activeEnhancements.trend || activeEnhancements.rarity))
        || (isMtb && mtbGroundTruthForecastSlot)
        || (!isMtb && (elevationData || hasSkiBands))) ? (
          <div data-house-section="tactical" className="mt-6 space-y-8">
            {activeEnhancements && (activeEnhancements.trend || activeEnhancements.rarity) && (
              <div className="flex flex-wrap items-center gap-2">
                <TrendBadge trend={activeEnhancements.trend} />
                <RarityBadge rarity={activeEnhancements.rarity} />
              </div>
            )}
            {!isMtb && (elevationData || hasSkiBands) ? (
              <DisclosurePanel title="Conditions by elevation">
                <div className="space-y-4">
                  {hasSkiBands && <SkiBandsPanel bySport={skiV3BySport} activeSport={activeSport} />}
                  {elevationData && (
                    <SkiElevationPanel data={elevationData} tierScores={elevationColumn.tierScores} bandsSportLabel={elevationColumn.sportLabel} />
                  )}
                </div>
              </DisclosurePanel>
            ) : null}
          </div>
        ) : null,

      outlook: !isMtb && upcomingDays.length > 0 ? (() => {
        const forecastDays = buildMountainForecastDays(upcomingDays, activeSport).map((day) => activeStale ? { ...day, scoreAvailable: false } : day);
        const best = forecastDays
          .filter((day) => day.scoreAvailable !== false)
          .reduce<SkiScoredDay | null>(
            (current, day) =>
              !current || day.score > current.score ? day : current,
            null,
          )?.date ?? null;
        return (
          <div id="section-forecast" data-house-section="outlook" className="mt-8 md:mt-10 bg-white rounded-[18px] border border-border-light p-5 md:p-7 scroll-mt-28">
            <div className="flex items-baseline justify-between mb-5">
              <h3 className="font-serif text-[20px] md:text-[24px] font-bold text-navy">Next 7 days</h3>
              <span className="text-[12px] text-text-muted">{forecastDays.every(d => d.weatherAvailable === false) ? "Seasonal estimate · weather unavailable" : "Forecast via Open-Meteo"}</span>
            </div>
            <SkiForecastStrip
              forecast={forecastDays}
              todayISO={todayISO}
              highlightDate={best ?? undefined}
              busyPeriodLabel={busyPeriodLabel}
            />
          </div>
        );
      })() : isMtb && upcomingDays.length > 0 ? (
        <div id="section-trail-forecast" data-house-section="outlook" className="mt-8 md:mt-10 bg-white rounded-[18px] border border-border-light p-5 md:p-7 scroll-mt-28">
          <h3 className="font-serif text-[20px] md:text-[24px] font-bold text-navy mb-4">7-day trail forecast</h3>
          <TrailForecastRow forecast={upcomingDays.map(d => ({
            date: d.date,
            rainMm: d.precipitationMm,
            tempMaxC: d.tempMaxC,
            windSpeedKmh: d.windSpeedKmh,
            weatherCode: d.weatherCode,
          }))} />
        </div>
      ) : null,

      planner: mapPanelSlot ? <MountainMapInvite /> : !activeStale && forecastPanelSlot ? <div data-house-section="planner">{forecastPanelSlot}</div> : null,

      packing: !activeStale && activePackingNotes.length > 0 ? (
        <div data-house-section="packing">
          <DisclosurePanel title="What to pack" id="section-packing">
            <ul className="space-y-1.5 text-[14px] leading-[1.7] text-text-body">
              {activePackingNotes.map((note, index) => (
                <li key={index} className="flex items-baseline gap-2.5">
                  <span aria-hidden className="h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full bg-gold" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </DisclosurePanel>
        </div>
      ) : null,
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
    </div>
  );

  /* ─── The spot house: air quality + server sections ─── */

  const spotPanel = (
    <div className="py-8 md:py-10">
      {orderLocationHouseSlots<"spot", ReactNode>("spot", {
      description: <SpotOverview description={description} facts={[
        { label: "Base elevation", value: skiDetail.baseElevationM == null ? null : `${skiDetail.baseElevationM} m` },
        { label: "Summit elevation", value: skiDetail.summitElevationM == null ? null : `${skiDetail.summitElevationM} m` },
      ]} />,
      "local-detail": spotLocalSlot,
      community: (
        <>
          {spotCommunitySlot}
          {isMtb && mtbGroundTruthCommunitySlot ? (
          <div id="section-mtb-ground-truth" data-house-section="community" className="mt-8 scroll-mt-28 md:mt-10">
            {mtbGroundTruthCommunitySlot}
          </div>
          ) : null}
        </>
      ),
      environment: airQuality && shouldShowAirQuality(airQuality) ? (
        <div data-house-section="environment">
          <DisclosurePanel title="Air quality" id="section-air-quality">
            <AirQualityPanel data={airQuality} />
          </DisclosurePanel>
        </div>
      ) : null,
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
    </div>
  );

  /* ─── Season house: heatmap + server sections ─── */

  const seasonPanel = (
    <div className="py-5 md:py-8">
      <SeasonOverview datasets={activeHeatmapDatasets} />
      {orderLocationHouseSlots<"season", ReactNode>("season", {
      "season-pattern": activeHeatmapDatasets.length > 0 ? (
        <DisclosurePanel title="Season pattern" id="section-season" defaultOpen>
          <SeasonHeatmap datasets={activeHeatmapDatasets} spotName={spotName} seasonWindow={seasonWindow ?? undefined} />
        </DisclosurePanel>
      ) : null,
      "recent-form": isMtb ? (
        <MountainMtbSeasonSlot
          name="recent-form"
          recentForm={mtbDetail?.recentForm ?? null}
          accuracy={forecastAccuracy}
          spotName={spotName}
        />
      ) : null,
      accuracy: isMtb ? (
        <MountainMtbSeasonSlot
          name="accuracy"
          recentForm={mtbDetail?.recentForm ?? null}
          accuracy={forecastAccuracy}
          spotName={spotName}
        />
      ) : null,
      additional: seasonPanelSlot,
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
    </div>
  );

  const forecastSummary = <div className="pt-5 md:pt-6">
      <ForecastStatus lastUpdated={activeUpdated} isStale={activeStale} forecastIssuedLabel={activeStale && activeBadgeIsGapForecast(badges, activeSport) ? gapForecast?.issuedLabel : null} />
      {!activeStale && <SeasonBanner perSportStatus={perSportSeasonStatus} activeSport={activeSport} resortName={spotName} />}
      {orderLocationHouseSlots<"aboveTabs", ReactNode>("aboveTabs", {
        "current-score": (
          <>
          <div data-house-section="current-score">
            {verdictClosed ? (
              <SkiStatusHeadline
                view={verdictClosed}
                metrics={perSportVerdict[verdictSport]?.metrics ?? []}
                touringAvailable={badges.some(({ sport }) => sport === "touring")}
                officialLink={officialLink}
              />
            ) : (
              <VerdictBlock defaultSport={defaultSport} perSport={verdictsWithGapForecast(perSportVerdict, badges, gapForecast)} enhancements={activeStale ? {} : universalEnhancements} perSportLuck={perSportLuck} availableSports={badges.map(({ sport }) => sport)} />
            )}
            {!activeStale && !verdictClosed && isSkiSportKey(verdictSport) && perSportVerdict[verdictSport]?.score != null && (
              <SkiHeadlineDetails
                view={verdictSkiView}
                extent={verdictSport === "piste" ? pisteExtent : null}
                officialLink={officialLink}
              />
            )}
          </div>
          {areaSlot ? <div data-house-section="better-nearby">{areaSlot}</div> : null}
          </>
        ),
        safety: (
          <div id="spot-safety" data-house-section="safety" className="scroll-mt-28">
            <SafetyHeadsUp defaultSport={defaultSport} perSport={perSportSafety} availableSports={badges.map(({ sport }) => sport)} />
          </div>
        ),
        "factor-summary": !activeStale && currentExplanation ? (
          <section className="pt-4" data-house-section="factor-summary">
            <FactorHeadlineSummary explanation={currentExplanation} />
          </section>
        ) : null,
        disclaimer: (
          <section className="pt-4">
            <SafetyDisclaimer />
          </section>
        ),
      }).map(({ slot, value }) => <Fragment key={slot}>{value}</Fragment>)}
      {!activeStale && <MountainSportFocus sport={activeSport} ski={skiDetail} mtb={mtbDetail} elevation={elevationData} offPiste={offPisteEnhancements} touring={touringEnhancements} drying={mtbEnhancements} country={country} reports={mtbGroundTruthForecastSlot} />}
      {(activeSport === "piste" || activeSport === "off-piste" || activeSport === "touring") &&
        <ExpandableMountainScene forecast={mountainForecast} sport={activeSport} />}
  </div>;

  const panels: Record<HouseId, ReactNode> = {
    forecast: <>{forecastSummary}{forecastPanel}</>,
    spot: spotPanel,
    season: seasonPanel,
  };

  return (
    <>
      <MtbGroundTruthLocationProvider locationSlug={locationSlug} enabled={isMtb}>
        <HouseTabs panels={panels} mapPanel={mapPanelSlot} toolbar={<SportSelector badges={badges} defaultSport={defaultSport} />} />
      </MtbGroundTruthLocationProvider>
    </>
  );
}

type MountainMtbSeasonSlotProps = {
  name: "recent-form" | "accuracy";
  recentForm: SurfRecentForm | null;
  accuracy: SpotAccuracy | null;
  spotName: string;
  defaultOpen?: boolean;
};

function UnavailableSeasonCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[16px] border border-border-light bg-cream p-5 md:p-7">
      <p className="text-[13px] font-semibold text-navy">Unavailable</p>
      <p className="mt-1 text-[13px] leading-[1.6] text-text-muted">{children}</p>
    </div>
  );
}

function MtbRecentForm({ recentForm }: { recentForm: SurfRecentForm | null }) {
  if (!recentForm || recentForm.daysLogged === 0) {
    return (
      <UnavailableSeasonCard>
        Recent form will appear once daily MTB scores have accumulated.
      </UnavailableSeasonCard>
    );
  }

  return (
    <div className="rounded-[18px] bg-cream px-5 py-8 md:px-8 md:py-10">
      <p className="mb-6 text-[13.5px] text-text-muted">
        Last {recentForm.daysLogged} days of logged MTB conditions.
      </p>
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <MtbRecentFormStat label="30-day average" value={scoreOutOf100Label(recentForm.avgScore)} />
        <MtbRecentFormStat label="Days firing" value={String(recentForm.daysFiring)} />
        <MtbRecentFormStat
          label="Best day recently"
          value={recentForm.bestDay ? scoreOutOf100Label(recentForm.bestDay.score) : "Unavailable"}
        />
        <MtbRecentFormStat label="Days logged" value={String(recentForm.daysLogged)} />
      </dl>
    </div>
  );
}

function MtbRecentFormStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[12px] border border-border-light bg-white p-4">
      <dt className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">{label}</dt>
      <dd className="mt-1 font-serif text-[22px] font-bold leading-tight text-navy tabular-nums md:text-[26px]">
        {value}
      </dd>
    </div>
  );
}

export function MountainMtbSeasonSlot({
  name,
  recentForm,
  accuracy,
  spotName,
  defaultOpen = false,
}: MountainMtbSeasonSlotProps) {
  if (name === "recent-form") {
    return (
      <div data-season-slot={name}>
        <DisclosurePanel title="Recent form" defaultOpen={defaultOpen}>
          <MtbRecentForm recentForm={recentForm} />
        </DisclosurePanel>
      </div>
    );
  }

  const hasAccuracy = accuracy !== null && accuracy.totalReports >= 5;
  return (
    <div data-season-slot={name}>
      <DisclosurePanel title="Forecast accuracy" defaultOpen={defaultOpen}>
        {hasAccuracy ? (
          <ForecastAccuracy accuracy={accuracy} spotName={spotName} />
        ) : (
          <UnavailableSeasonCard>
            Forecast accuracy will appear once enough verified MTB forecast history has accumulated.
          </UnavailableSeasonCard>
        )}
      </DisclosurePanel>
    </div>
  );
}
